import { useEffect, useRef } from 'react';

const createBroadcastChannel = (channelName: string) => {
	try {
		// eslint-disable-next-line compat/compat
		return new BroadcastChannel(channelName);
	} catch {
		return null;
	}
};

export const useBroadcastChannel = <MessageTypes>(
	channelName: string,
	callback: (message: MessageEvent<MessageTypes>) => void,
): {
	postMessage: (message: MessageTypes) => void;
} => {
	const channel = useRef<BroadcastChannel | null>(null);
	const savedCallback = useRef(callback);

	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	useEffect(() => {
		if (channel.current) {
			channel.current.removeEventListener('message', savedCallback.current);
			channel.current.close();
			channel.current = null;
		}
		channel.current = createBroadcastChannel(channelName);
		if (channel.current) {
			channel.current.addEventListener('message', (event: MessageEvent<MessageTypes>) => {
				savedCallback.current(event);
			});
		}
		return () => {
			if (channel.current) {
				channel.current.removeEventListener('message', savedCallback.current);
				channel.current.close();
				channel.current = null;
			}
		};
	}, [channelName]);

	if (channel.current) {
		return {
			postMessage: (message: MessageTypes) => {
				channel.current?.postMessage(message);
			},
		};
	}

	return {
		postMessage: () => {},
	};
};
